/**
 * gallery_controller.js — Photos card behaviour for quote/job detail (Stimulus).
 *
 * Ports static/js/photo_gallery.js (upload, lightbox, swipe nav, inline caption
 * edit, delete, drag-reorder) to an element-scoped controller on .photos-card
 * (data-controller="gallery"), replacing the attachOnce() once-guard + manual
 * turbo:before-cache overlay strip.
 *
 * The behaviour is document-delegated (the lightbox is a <body> overlay; thumbs,
 * caption, swipe and drag are matched by closest()), so connect() binds the
 * document/window listeners and disconnect() removes them — the controller
 * instance owning add/remove is what the attachOnce guard hand-rolled. One
 * .photos-card renders per detail page, so there is a single listener set.
 *
 * Backing API: /api/v1/attachments. Template: components/detail/entity_photos.html.
 *
 * Offline capture queue: uploads attempted while offline (or failing with a
 * network error) are queued in the PowerSync local-only table
 * pending_photo_captures (static/js/powersync/schema.js) with bytes held by
 * the platform byte adapter (window.ChippieBridge.photoStorage — the native
 * app container on the shells, OPFS on the PWA; in-session Map fallback
 * here). flushPendingCaptures() re-POSTs them on reconnect; the server
 * dedupes retries via client_capture_uuid, so a flush can never double-insert.
 * Degrades to online-only when PowerSync is unavailable.
 *
 * SCOPE LIMIT: the queue is only serviced while a page carrying a .photos-card
 * is mounted — capture offline on a job, navigate to the dashboard, reconnect,
 * and nothing uploads until a photos-bearing detail page is opened online. The
 * bytes and the row are safe until then; this is a known limit, not a bug.
 */

import { Controller } from "@hotwired/stimulus";

    var LIGHTBOX_HISTORY_KEY = 'chippieLightbox';
    var SETTLE_CLASS = 'photo-lightbox__track--settling';

    function csrfHeaders() {
        var token = (typeof FormValidator !== 'undefined' && FormValidator.getCsrfToken)
            ? FormValidator.getCsrfToken() : null;
        return token ? { 'X-CSRFToken': token } : {};
    }

    function cardFor(el) {
        return el && el.closest ? el.closest('.photos-card') : null;
    }

    function thumbsIn(card) {
        return Array.prototype.slice.call(card.querySelectorAll('.photo-thumb'));
    }

    async function uploadFile(card, file, captureUuid) {
        var fd = new FormData();
        fd.append('file', file);
        fd.append('entity_type', card.dataset.entityType);
        fd.append('entity_id', card.dataset.entityId);
        fd.append('kind', 'image');
        if (captureUuid) fd.append('client_capture_uuid', captureUuid);
        var resp = await fetch(card.dataset.uploadUrl, {
            method: 'POST', body: fd, headers: csrfHeaders(), credentials: 'same-origin'
        });
        if (!resp.ok) {
            var msg = 'Upload failed';
            try { msg = (await resp.json()).error || msg; } catch (_) {}
            throw new Error(msg);
        }
        return resp.json();
    }

    function newCaptureUuid() {
        if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
        return 'cap-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
    }

    /**
     * Byte-storage adapter seam for offline captures: the photo-storage bridge
     * component (bridge/photo_storage_controller.js — native app container on
     * the shells, OPFS on the PWA) via the ChippieBridge facade. The
     * in-session Map is the last-resort degrade when the facade is absent
     * (bytes lost on reload — the pending row is then dropped at flush).
     */
    var memoryBytes = new Map();
    function bytesAdapter() {
        var bridge = window.ChippieBridge && window.ChippieBridge.photoStorage;
        if (bridge && bridge.save) {
            return {
                put: function (key, blob) { return bridge.save(key, blob); },
                get: function (key) { return bridge.read(key); },
                remove: function (key) { return bridge.delete(key); },
            };
        }
        return {
            put: function (key, blob) { memoryBytes.set(key, blob); return Promise.resolve(); },
            get: function (key) { return Promise.resolve(memoryBytes.get(key) || null); },
            remove: function (key) { memoryBytes.delete(key); return Promise.resolve(); },
        };
    }

    /**
     * Match the server-rendered overflow state after a mutation: strip gets
     * --has-overflow at >4 photos, the 4th thumb gets a "+N" badge, earlier
     * badges clear when the count drops to ≤4.
     */
    function recomputeOverflow(card) {
        var strip = card.querySelector('.photos-strip');
        if (!strip) return;
        var thumbs = thumbsIn(card);
        var extra = thumbs.length - 4;

        strip.classList.toggle('photos-strip--has-overflow', extra > 0);

        thumbs.forEach(function (t, idx) {
            var isFourth = idx === 3;
            t.classList.toggle('photo-thumb--overflow', isFourth && extra > 0);
            var existing = t.querySelector('.photo-thumb__more');
            if (isFourth && extra > 0) {
                if (existing) {
                    existing.textContent = '+' + extra;
                } else {
                    var label = document.createElement('span');
                    label.className = 'photo-thumb__more';
                    label.textContent = '+' + extra;
                    t.appendChild(label);
                }
                t.dataset.overflowCount = extra;
            } else {
                if (existing) existing.remove();
                delete t.dataset.overflowCount;
            }
        });
    }

    // A strip tile loads the small copy, never the original (the lightbox
    // loads that, by id). The API names the URL; the fallback covers a row
    // shape without it.
    function thumbUrl(data) {
        return data.thumb_url || ('/api/v1/attachments/' + data.id + '/file?size=thumb');
    }

    // The "Show to client" tick for one photo, cloned from the card's
    // <template>: the share_toggle macro in entity_photos.html owns the markup,
    // photo_share_controller.js the behaviour.
    function shareToggle(card, attachmentId, shared) {
        var tpl = card.querySelector('.photo-share-template');
        if (!tpl) return null;
        var el = tpl.content.firstElementChild.cloneNode(true);
        el.dataset.photoShareIdValue = attachmentId;
        el.classList.toggle('is-on', !!shared);
        el.setAttribute('aria-pressed', shared ? 'true' : 'false');
        return el;
    }

    // A photo's saved tick, read off its tile's toggle (the lightbox has no
    // state of its own).
    function isShared(card, attachmentId) {
        var toggle = card.querySelector('.photo-thumb[data-attachment-id="' + attachmentId + '"] .photo-share');
        return !!toggle && toggle.classList.contains('is-on');
    }

    // The lightbox's copy of a photo's tick. A photo the client already sees
    // inline carries a fixed marker on its tile instead (placed_marker in
    // entity_photos.html), and the lightbox shows that same marker.
    function lightboxShareControl(card, attachmentId) {
        var placed = card.querySelector('.photo-thumb[data-attachment-id="' + attachmentId + '"] .photo-share--placed');
        return placed ? placed.cloneNode(true) : shareToggle(card, attachmentId, isShared(card, attachmentId));
    }

    function appendShareToggle(card, thumb, data) {
        var toggle = shareToggle(card, data.id, data.client_visible);
        if (toggle) thumb.appendChild(toggle);
    }

    function appendThumb(card, data) {
        var strip = card.querySelector('.photos-strip');
        if (!strip) return;
        var empty = strip.querySelector('.photos-empty');
        if (empty) empty.remove();
        var thumb = document.createElement('div');
        thumb.className = 'photo-thumb';
        thumb.dataset.attachmentId = data.id;
        thumb.dataset.caption = data.caption || '';
        thumb.draggable = true;
        var img = document.createElement('img');
        img.src = thumbUrl(data);
        img.alt = data.caption || data.original_filename || '';
        img.loading = 'lazy';
        thumb.appendChild(img);
        appendShareToggle(card, thumb, data);
        strip.appendChild(thumb);
        recomputeOverflow(card);
    }

    /**
     * Queued-offline render state: greyed thumb showing the local bytes,
     * excluded from lightbox/drag (no attachmentId). resolvePendingThumb
     * upgrades it in place once the flush POST returns the real row.
     */
    // Object URLs minted for queued thumbs, keyed by capture uuid. Turbo never
    // unloads the document, so a thumb discarded with the old <body> takes its
    // URL out of reach of the resolve/remove revoke paths — and every revisit
    // repaints, minting another. Tracking them here lets a repaint revoke the
    // previous URL for the same capture instead of leaking one per visit.
    var pendingThumbUrls = new Map();

    function revokePendingThumbUrl(captureUuid) {
        var url = pendingThumbUrls.get(captureUuid);
        if (!url) return;
        URL.revokeObjectURL(url);
        pendingThumbUrls.delete(captureUuid);
    }

    function appendPendingThumb(card, file, captureUuid) {
        var strip = card.querySelector('.photos-strip');
        if (!strip) return;
        var empty = strip.querySelector('.photos-empty');
        if (empty) empty.remove();
        var thumb = document.createElement('div');
        thumb.className = 'photo-thumb photo-thumb--pending';
        thumb.dataset.captureUuid = captureUuid;
        var img = document.createElement('img');
        revokePendingThumbUrl(captureUuid);
        var url = URL.createObjectURL(file);
        pendingThumbUrls.set(captureUuid, url);
        img.src = url;
        img.alt = 'Queued photo';
        thumb.appendChild(img);
        strip.appendChild(thumb);
        recomputeOverflow(card);
    }

    /** Upload filename for a queued capture — extension follows the real mime
     *  so a PNG/HEIC capture is not stored as .jpg. */
    function captureFilename(row, blob) {
        if (blob.name) return blob.name;
        var subtype = (blob.type || '').split('/')[1] || 'jpg';
        return 'capture-' + row.client_capture_uuid + '.' + subtype.split('+')[0];
    }

    function slideMarkup(attachmentId) {
        return '<div class="photo-lightbox__slide">' +
            '<img class="photo-lightbox__image" src="/api/v1/attachments/' + attachmentId + '/file" alt="">' +
            '</div>';
    }

    async function saveCaption(card, id, newText) {
        var resp = await fetch('/api/v1/attachments/' + id, {
            method: 'PATCH',
            headers: Object.assign({ 'Content-Type': 'application/json' }, csrfHeaders()),
            body: JSON.stringify({ caption: newText }),
            credentials: 'same-origin'
        });
        if (!resp.ok) throw new Error('Caption save failed');
        var thumb = card.querySelector('.photo-thumb[data-attachment-id="' + id + '"]');
        if (thumb) thumb.dataset.caption = newText;
    }

    async function persistOrder(card) {
        var ids = thumbsIn(card)
            .filter(function (t) { return t.dataset.attachmentId; })
            .map(function (t) { return parseInt(t.dataset.attachmentId, 10); });
        var body = {
            entity_type: card.dataset.entityType,
            entity_id: parseInt(card.dataset.entityId, 10),
            ordered_ids: ids
        };
        try {
            var resp = await fetch(card.dataset.reorderUrl, {
                method: 'POST',
                headers: Object.assign({ 'Content-Type': 'application/json' }, csrfHeaders()),
                body: JSON.stringify(body),
                credentials: 'same-origin'
            });
            if (!resp.ok) console.warn('[gallery] reorder failed:', resp.status);
        } catch (err) {
            console.error('[gallery] reorder error:', err);
        }
    }

    function settleTrack(track, targetPercent, after) {
        track.classList.add(SETTLE_CLASS);
        track.style.transform = 'translateX(' + targetPercent + ')';
        var fallback;
        var done = function () {
            clearTimeout(fallback);
            track.removeEventListener('transitionend', done);
            track.classList.remove(SETTLE_CLASS);
            if (after) after();
        };
        // transitionend won't fire if the value didn't change; guard with a timeout.
        fallback = setTimeout(done, 320);
        track.addEventListener('transitionend', done);
    }

export default class extends Controller {
        connect() {
            this.lightboxState = { card: null, index: 0, ids: [] };
            this.swipe = { track: null, startX: 0, width: 1, startT: 0, dx: 0, active: false };
            this.dragSource = null;

            this._listeners = [
                [document, 'change', this.onChange.bind(this)],
                [document, 'click', this.onClick.bind(this)],
                [document, 'focusin', this.onFocusin.bind(this)],
                [document, 'blur', this.onBlur.bind(this), true],
                [document, 'keydown', this.onKeydown.bind(this)],
                [window, 'popstate', this.onPopstate.bind(this)],
                [document, 'turbo:before-cache', this.onBeforeCache.bind(this)],
                [document, 'touchstart', this.onTouchStart.bind(this), { passive: true }],
                [document, 'touchmove', this.onTouchMove.bind(this), { passive: true }],
                [document, 'touchend', this.onTouchEnd.bind(this)],
                [document, 'dragstart', this.onDragStart.bind(this)],
                [document, 'dragover', this.onDragOver.bind(this)],
                [document, 'drop', this.onDrop.bind(this)],
                [document, 'dragend', this.onDragEnd.bind(this)],
                [window, 'online', this.flushPendingCaptures.bind(this)],
                [document, 'powersync:ready', this.syncQueuedCaptures.bind(this)],
            ];
            this._listeners.forEach(([t, e, fn, opt]) => t.addEventListener(e, fn, opt));
            this._flushing = false;
            this.syncQueuedCaptures();
        }

        disconnect() {
            if (this._listeners) {
                this._listeners.forEach(([t, e, fn, opt]) => t.removeEventListener(e, fn, opt));
                this._listeners = null;
            }
            this.closeLightbox(true);
        }

        // --- Lightbox history ---
        lightboxHistoryActive() {
            return !!(history.state && history.state[LIGHTBOX_HISTORY_KEY]);
        }

        openLightbox(card, startId) {
            var thumbs = thumbsIn(card).filter(function (t) { return t.dataset.attachmentId; });
            if (!thumbs.length) return;
            this.lightboxState.card = card;
            this.lightboxState.ids = thumbs.map(function (t) { return parseInt(t.dataset.attachmentId, 10); });
            this.lightboxState.index = Math.max(0, this.lightboxState.ids.indexOf(parseInt(startId, 10)));
            if (!this.lightboxHistoryActive()) {
                var state = {};
                state[LIGHTBOX_HISTORY_KEY] = true;
                history.pushState(state, '');
            }
            this.renderLightbox();
        }

        currentCaption() {
            var card = this.lightboxState.card;
            var id = this.lightboxState.ids[this.lightboxState.index];
            var thumb = card.querySelector('.photo-thumb[data-attachment-id="' + id + '"]');
            return thumb ? (thumb.dataset.caption || '') : '';
        }

        renderLightbox() {
            this.closeLightbox(true);
            var n = this.lightboxState.ids.length;
            var id = this.lightboxState.ids[this.lightboxState.index];
            // Neighbours fill the carousel track so a swipe reveals the adjacent
            // photo under the finger. The list wraps, so neighbours always exist.
            var prevId = this.lightboxState.ids[(this.lightboxState.index - 1 + n) % n];
            var nextId = this.lightboxState.ids[(this.lightboxState.index + 1) % n];
            var overlay = document.createElement('div');
            overlay.className = 'photo-lightbox';
            overlay.innerHTML =
                '<div class="photo-lightbox__topbar">' +
                    '<span class="photo-lightbox__counter">' + (this.lightboxState.index + 1) + ' / ' + this.lightboxState.ids.length + '</span>' +
                    '<div class="photo-lightbox__topbar-actions">' +
                        '<button type="button" data-action="lightbox-delete" title="Delete"><i class="bi bi-trash"></i></button>' +
                        '<button type="button" data-action="lightbox-close" title="Close"><i class="bi bi-x-lg"></i></button>' +
                    '</div>' +
                '</div>' +
                '<div class="photo-lightbox__stage">' +
                    '<button type="button" class="photo-lightbox__nav photo-lightbox__nav--prev" data-action="lightbox-prev"><i class="bi bi-chevron-left"></i></button>' +
                    '<div class="photo-lightbox__track">' +
                        slideMarkup(prevId) + slideMarkup(id) + slideMarkup(nextId) +
                    '</div>' +
                    '<button type="button" class="photo-lightbox__nav photo-lightbox__nav--next" data-action="lightbox-next"><i class="bi bi-chevron-right"></i></button>' +
                '</div>' +
                '<div class="photo-lightbox__caption">' +
                    '<span contenteditable="true" data-action="lightbox-caption">' +
                        (this.currentCaption().replace(/[&<>"']/g, function (c) {
                            return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
                        }) || 'Add a caption…') +
                    '</span>' +
                '</div>';
            var card = this.lightboxState.card;
            var toggle = lightboxShareControl(card, id);
            if (toggle) overlay.querySelector('.photo-lightbox__topbar-actions').prepend(toggle);
            document.body.appendChild(overlay);
        }

        closeLightbox(silent) {
            var existing = document.querySelector('.photo-lightbox');
            if (existing) existing.remove();
            if (!silent) {
                this.lightboxState.card = null;
                this.lightboxState.ids = [];
                this.lightboxState.index = 0;
                // Consume the marker entry pushed on open. Skipped when this close
                // was itself triggered by popstate — the entry is already gone.
                if (this.lightboxHistoryActive()) history.back();
            }
        }

        nextPhoto() {
            if (!this.lightboxState.ids.length) return;
            this.lightboxState.index = (this.lightboxState.index + 1) % this.lightboxState.ids.length;
            this.renderLightbox();
        }

        prevPhoto() {
            if (!this.lightboxState.ids.length) return;
            this.lightboxState.index = (this.lightboxState.index - 1 + this.lightboxState.ids.length) % this.lightboxState.ids.length;
            this.renderLightbox();
        }

        async deleteCurrent() {
            var id = this.lightboxState.ids[this.lightboxState.index];
            var card = this.lightboxState.card;
            if (!confirm('Delete this photo?')) return;
            var resp = await fetch('/api/v1/attachments/' + id, {
                method: 'DELETE', headers: csrfHeaders(), credentials: 'same-origin'
            });
            if (!resp.ok) {
                alert('Failed to delete photo');
                return;
            }
            var thumb = card.querySelector('.photo-thumb[data-attachment-id="' + id + '"]');
            if (thumb) thumb.remove();
            recomputeOverflow(card);
            this.lightboxState.ids.splice(this.lightboxState.index, 1);
            if (!this.lightboxState.ids.length) {
                this.closeLightbox();
                return;
            }
            this.lightboxState.index = this.lightboxState.index % this.lightboxState.ids.length;
            this.renderLightbox();
        }

        // --- Offline capture queue ---
        /**
         * Is queueing this capture allowed? The local-first classification is
         * DATA, owned by powersync-entities.js — this seam consults it rather
         * than deciding for itself, so the registry's claim to be the single
         * owner of "is this write local-first?" is actually true.
         */
        captureQueueAvailable(card) {
            if (!window.chippiePowerSync) return false;
            if (typeof window.isLocalFirstOperation !== 'function') return false;
            var entity = card && card.dataset ? card.dataset.entityType : null;
            return window.isLocalFirstOperation(entity, 'photo_upload');
        }

        async enqueueCapture(card, file, captureUuid) {
            var db = window.chippiePowerSync;
            var key = 'capture/' + captureUuid;
            var stored = await bytesAdapter().put(key, file);
            if (stored === false) {
                // The byte container reported a failed write (disk full, IO
                // error — both native impls resolve false rather than
                // rejecting). A queued row with no bytes behind it paints a
                // "queued" thumb the next flush silently deletes, and the
                // capture is the SOLE copy of the photo — fail loud instead.
                console.error('[gallery] photo byte store rejected capture', key);
                if (typeof window.showNotification === 'function') {
                    window.showNotification(
                        'Could not save this photo for offline upload. Free up storage and try again.',
                        'error'
                    );
                }
                return;
            }
            await db.execute(
                "INSERT INTO pending_photo_captures " +
                "(id, entity_type, entity_id, caption, sort_order, upload_state, local_key, client_capture_uuid, created_at) " +
                "VALUES (uuid(), ?, ?, NULL, 0, 'pending', ?, ?, datetime('now'))",
                [card.dataset.entityType, card.dataset.entityId, key, captureUuid]
            );
            appendPendingThumb(card, file, captureUuid);
        }

        /**
         * Repaint queued-capture thumbs from the local queue, then flush.
         * The enqueue-time thumb only exists in the DOM that captured it —
         * navigating away and back (a cached copy, offline) paints the
         * server's strip, which predates the capture. Without this rebuild
         * the queued photo LOOKS lost even though its row and bytes are
         * intact, so every connect and PowerSync init re-derives the pending
         * thumbs from pending_photo_captures + the byte store.
         */
        async syncQueuedCaptures() {
            var db = window.chippiePowerSync;
            if (!db) return;
            // Latch: this runs from connect(), from powersync:ready, and
            // alongside the online-triggered flush. Two passes overlapping
            // means two concurrent bridge reads, and the iOS bridge replies
            // only to the LAST message per event name — the earlier read's
            // promise never settles, stranding this pass (and, before the
            // flush latch was reachable, the flush with it).
            if (this._syncing) return;
            this._syncing = true;
            try {
                var cards = document.querySelectorAll('.photos-card[data-entity-type][data-entity-id]');
                for (var card of cards) {
                    var rows = await db.getAll(
                        "SELECT * FROM pending_photo_captures WHERE upload_state = 'pending' " +
                        'AND entity_type = ? AND entity_id = ? ORDER BY created_at',
                        [card.dataset.entityType, card.dataset.entityId]
                    );
                    for (var row of rows) {
                        if (document.querySelector('.photo-thumb--pending[data-capture-uuid="' + row.client_capture_uuid + '"]')) continue;
                        var blob = await bytesAdapter().get(row.local_key);
                        // Bytes gone (in-session fallback): nothing to show — the
                        // next online flush drops the orphaned row.
                        if (!blob) continue;
                        // Re-check after the await: the byte read yields, so a
                        // concurrent trigger could have painted this thumb in
                        // the meantime and we would double it.
                        if (document.querySelector('.photo-thumb--pending[data-capture-uuid="' + row.client_capture_uuid + '"]')) continue;
                        appendPendingThumb(card, blob, row.client_capture_uuid);
                    }
                }
            } catch (err) {
                console.error('[gallery] queued-capture repaint failed', err);
            } finally {
                this._syncing = false;
            }
            this.flushPendingCaptures();
        }

        /**
         * Upload every queued capture, oldest first. Runs on reconnect,
         * on PowerSync init, and once per controller connect; the _flushing
         * latch stops overlapping runs from double-POSTing (and the server's
         * client_capture_uuid dedupe covers retries across sessions).
         */
        async flushPendingCaptures() {
            if (this._flushing) return;
            var db = window.chippiePowerSync;
            if (!db || !navigator.onLine) return;
            this._flushing = true;
            try {
                var rows = await db.getAll(
                    "SELECT * FROM pending_photo_captures WHERE upload_state = 'pending' ORDER BY created_at"
                );
                for (var row of rows) {
                    var blob = await bytesAdapter().get(row.local_key);
                    if (!blob) {
                        // Bytes did not survive (in-session fallback after a
                        // reload) — nothing recoverable; drop the pending row.
                        await this.discardCapture(db, row);
                        continue;
                    }
                    try {
                        var fd = new FormData();
                        fd.append('file', blob, captureFilename(row, blob));
                        fd.append('entity_type', row.entity_type);
                        fd.append('entity_id', row.entity_id);
                        fd.append('kind', 'image');
                        if (row.caption) fd.append('caption', row.caption);
                        fd.append('client_capture_uuid', row.client_capture_uuid);
                        var resp = await fetch('/api/v1/attachments', {
                            method: 'POST', body: fd, headers: csrfHeaders(), credentials: 'same-origin'
                        });
                        if (!resp.ok) {
                            if (resp.status >= 400 && resp.status < 500) {
                                // Permanent: the entity was deleted, the file
                                // is too large, a plan limit is hit. Retrying
                                // can never succeed, so the row would re-POST
                                // on every reconnect forever and its bytes
                                // would never be freed. Drop it and say so.
                                await this.discardCapture(db, row);
                                if (typeof window.showNotification === 'function') {
                                    window.showNotification(
                                        'A photo saved offline could not be uploaded and was discarded.',
                                        'warning'
                                    );
                                }
                                continue;
                            }
                            continue;   // 5xx — transient, retry next flush
                        }
                        var data = await resp.json();
                        // Bytes first: a kill between the two leaves the row
                        // to re-POST (idempotent server-side via
                        // client_capture_uuid), whereas the reverse order
                        // orphans the container file forever — there is no
                        // list API, so nothing could ever sweep it.
                        await bytesAdapter().remove(row.local_key);
                        await db.execute('DELETE FROM pending_photo_captures WHERE id = ?', [row.id]);
                        this.resolvePendingThumb(row.client_capture_uuid, data);
                    } catch (_err) {
                        // Network dropped again mid-flush — row stays queued.
                        break;
                    }
                }
            } finally {
                this._flushing = false;
            }
        }

        /** Drop a queued capture and everything that belongs to it. */
        async discardCapture(db, row) {
            await bytesAdapter().remove(row.local_key);
            await db.execute('DELETE FROM pending_photo_captures WHERE id = ?', [row.id]);
            this.removePendingThumb(row.client_capture_uuid);
        }

        resolvePendingThumb(captureUuid, data) {
            // Revoke before the null check: if Turbo swapped the thumb away,
            // its URL is unreachable from the DOM but still tracked here.
            revokePendingThumbUrl(captureUuid);
            var thumb = document.querySelector('.photo-thumb--pending[data-capture-uuid="' + captureUuid + '"]');
            if (!thumb) return;
            var card = cardFor(thumb);
            thumb.classList.remove('photo-thumb--pending');
            thumb.dataset.attachmentId = data.id;
            thumb.dataset.caption = data.caption || '';
            delete thumb.dataset.captureUuid;
            thumb.draggable = true;
            var img = thumb.querySelector('img');
            if (img) {
                if (img.src && img.src.indexOf('blob:') === 0) URL.revokeObjectURL(img.src);
                img.src = thumbUrl(data);
            }
            if (card) {
                appendShareToggle(card, thumb, data);
                recomputeOverflow(card);
            }
        }

        removePendingThumb(captureUuid) {
            revokePendingThumbUrl(captureUuid);
            var thumb = document.querySelector('.photo-thumb--pending[data-capture-uuid="' + captureUuid + '"]');
            if (!thumb) return;
            var card = cardFor(thumb);
            var img = thumb.querySelector('img');
            if (img && img.src && img.src.indexOf('blob:') === 0) URL.revokeObjectURL(img.src);
            thumb.remove();
            if (card) recomputeOverflow(card);
        }

        async uploadOrQueue(card, file) {
            var captureUuid = newCaptureUuid();
            if (!navigator.onLine && this.captureQueueAvailable(card)) {
                return this.enqueueCapture(card, file, captureUuid);
            }
            try {
                var data = await uploadFile(card, file, captureUuid);
                appendThumb(card, data);
            } catch (err) {
                // fetch() rejects with TypeError on network failure — the
                // offline case navigator.onLine missed (captive portal, drop
                // mid-request). Server-side rejections are real Errors.
                if (err instanceof TypeError && this.captureQueueAvailable(card)) {
                    return this.enqueueCapture(card, file, captureUuid);
                }
                console.error('[gallery] upload failed:', err);
                if (typeof window.showNotification === 'function') {
                    window.showNotification(err.message, 'error');
                } else {
                    alert(err.message);
                }
            }
        }

        // --- Event handlers ---
        onChange(event) {
            var input = event.target;
            if (!input.matches || !input.matches('input[data-action="upload-photo"]')) return;
            var card = cardFor(input);
            if (!card) return;
            var files = Array.from(input.files || []);
            input.value = '';
            files.forEach((file) => { this.uploadOrQueue(card, file); });
        }

        onClick(event) {
            var thumb = event.target.closest('.photos-strip .photo-thumb');
            if (thumb) {
                // Pending (queued-offline) thumbs have no attachment yet —
                // nothing to show in the lightbox.
                if (!thumb.dataset.attachmentId) return;
                var card = cardFor(thumb);
                if (card) this.openLightbox(card, thumb.dataset.attachmentId);
                return;
            }

            var actionEl = event.target.closest('[data-action]');
            if (!actionEl) return;

            // Action-bar "Add Photo" tile: route to the hidden file input on the
            // matching photos card so we share one upload path.
            if (actionEl.dataset.action === 'trigger-photo-upload') {
                event.preventDefault();
                var et = actionEl.dataset.entityType;
                var ei = actionEl.dataset.entityId;
                var input = document.getElementById(et + '-' + ei + '-photo-input');
                if (input) input.click();
                return;
            }

            switch (actionEl.dataset.action) {
                case 'lightbox-close':
                    event.preventDefault();
                    this.closeLightbox();
                    break;
                case 'lightbox-next':
                    event.preventDefault();
                    this.nextPhoto();
                    break;
                case 'lightbox-prev':
                    event.preventDefault();
                    this.prevPhoto();
                    break;
                case 'lightbox-delete':
                    event.preventDefault();
                    this.deleteCurrent();
                    break;
            }
        }

        onFocusin(event) {
            var el = event.target;
            if (el.matches && el.matches('[data-action="lightbox-caption"]')) {
                if (el.textContent === 'Add a caption…') el.textContent = '';
            }
        }

        onBlur(event) {
            var el = event.target;
            if (!el.matches || !el.matches('[data-action="lightbox-caption"]')) return;
            var text = (el.textContent || '').trim();
            var id = this.lightboxState.ids[this.lightboxState.index];
            var card = this.lightboxState.card;
            if (card == null || id == null) return;
            saveCaption(card, id, text).catch(function (err) {
                console.error('[gallery] caption save failed:', err);
            });
        }

        onKeydown(event) {
            if (!document.querySelector('.photo-lightbox')) return;
            if (event.key === 'Escape') this.closeLightbox();
            else if (event.key === 'ArrowRight') this.nextPhoto();
            else if (event.key === 'ArrowLeft') this.prevPhoto();
        }

        onPopstate() {
            // Pop landed off the marker entry, so just tear the overlay down.
            // lightboxHistoryActive() is now false, so closeLightbox() won't
            // call history.back() again.
            if (document.querySelector('.photo-lightbox')) this.closeLightbox();
        }

        onBeforeCache() {
            // Strip the overlay before Turbo snapshots, otherwise the cached
            // snapshot includes the open lightbox and the photo "reopens" on restore.
            var existing = document.querySelector('.photo-lightbox');
            if (existing) existing.remove();
        }

        // --- Swipe: drag the 3-slide track 1:1 with the finger ---
        onTouchStart(event) {
            var stage = event.target.closest('.photo-lightbox__stage');
            if (!stage || this.lightboxState.ids.length < 2) return;
            var track = stage.querySelector('.photo-lightbox__track');
            if (!track) return;
            this.swipe.track = track;
            this.swipe.startX = event.touches[0].clientX;
            this.swipe.width = stage.clientWidth || 1;
            this.swipe.startT = Date.now();
            this.swipe.dx = 0;
            this.swipe.active = true;
            track.classList.remove(SETTLE_CLASS);
        }

        onTouchMove(event) {
            if (!this.swipe.active) return;
            this.swipe.dx = event.touches[0].clientX - this.swipe.startX;
            this.swipe.track.style.transform = 'translateX(calc(-33.3333% + ' + this.swipe.dx + 'px))';
        }

        onTouchEnd() {
            if (!this.swipe.active) return;
            this.swipe.active = false;
            var track = this.swipe.track;
            var dx = this.swipe.dx;
            var velocity = dx / Math.max(1, Date.now() - this.swipe.startT); // px per ms
            // Commit on a long drag (past a quarter width) or a fast flick.
            var committed = Math.abs(dx) > this.swipe.width * 0.25
                || (Math.abs(velocity) > 0.4 && Math.abs(dx) > 20);

            if (committed && dx < 0) {
                settleTrack(track, '-66.6666%', () => this.nextPhoto());
            } else if (committed && dx > 0) {
                settleTrack(track, '0%', () => this.prevPhoto());
            } else if (Math.abs(dx) > 2) {
                settleTrack(track, '-33.3333%', null); // cancelled — spring back
            } else {
                track.style.transform = ''; // tap, not a drag — drop inline override
            }
        }

        // --- Drag reorder ---
        onDragStart(event) {
            var thumb = event.target.closest('.photos-strip .photo-thumb');
            if (!thumb) return;
            this.dragSource = thumb;
            thumb.classList.add('dragging');
            event.dataTransfer.effectAllowed = 'move';
        }

        onDragOver(event) {
            var target = event.target.closest('.photos-strip .photo-thumb');
            if (!target || !this.dragSource || target === this.dragSource) return;
            event.preventDefault();
            document.querySelectorAll('.photo-thumb.drop-target').forEach(function (el) {
                el.classList.remove('drop-target');
            });
            target.classList.add('drop-target');
        }

        onDrop(event) {
            var target = event.target.closest('.photos-strip .photo-thumb');
            if (!target || !this.dragSource || target === this.dragSource) return;
            event.preventDefault();
            var card = cardFor(target);
            var strip = card.querySelector('.photos-strip');
            strip.insertBefore(this.dragSource, target);
            persistOrder(card);
        }

        onDragEnd() {
            if (this.dragSource) this.dragSource.classList.remove('dragging');
            document.querySelectorAll('.photo-thumb.drop-target').forEach(function (el) {
                el.classList.remove('drop-target');
            });
            this.dragSource = null;
        }
}
